import React, { useState, useEffect, useCallback, useRef } from 'react';
import { updateFollowUp } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { anyModalOpen } from '../crm/modalGuard';
import { FiPhone, FiClock, FiCheckCircle, FiRefreshCw, FiEye, FiLock } from 'react-icons/fi';
import CallDetailModal from '../components/CallDetailModal';
import FollowUpModal from '../components/FollowUpModal';
// --- CRM layer (added, additive) -------------------------------------------
// Same two columns as CallsPage, reusing the very same components — no logic
// is duplicated here.
import CrmCallCell from '../crm/CrmCallCell';
import CrmTransferCell from '../crm/CrmTransferCell';
import { crmListOpportunities, crmListClients, crmGo, CLOSED_STAGES } from '../crm/crmApi';
// ---------------------------------------------------------------------------

const QUICK_FILTERS = [
  { key: 'today', label: 'اليوم' },
  { key: 'yesterday', label: 'أمس' },
  { key: '7days', label: 'آخر 7 أيام' },
  { key: 'month', label: 'هذا الشهر' },
  { key: 'all', label: 'الكل' }
];

const computeRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'today': return { from: today.toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'yesterday': { const y = new Date(today.getTime() - 86400000); return { from: y.toISOString(), to: today.toISOString() }; }
    case '7days': return { from: new Date(today.getTime() - 7 * 86400000).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'month': return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    default: return { from: '', to: '' };
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date} ${time}`;
};

const getStatusBadge = (s) => {
  const map = {
    answered: { label: 'مردود عليها', cls: 'badge-answered' },
    missed_in_queue: { label: 'فائتة في الكيو', cls: 'badge-missed' },
    missed_before_queue: { label: 'أغلق أثناء رسالة الترحيب', cls: 'badge-missed' },
    abandoned: { label: 'لم يتم الرد وأغلق العميل', cls: 'badge-abandoned' },
    after_hours: { label: 'خارج الدوام', cls: 'badge-after-hours' },
    voicemail: { label: 'بريد صوتي', cls: 'badge-voicemail' }
  };
  const info = map[s] || { label: s, cls: '' };
  return <span className={`badge ${info.cls}`}>{info.label}</span>;
};

const getFollowUpBadge = (s, autoDetected, followUpBy) => {
  const map = {
    not_required: { label: 'لا يلزم', cls: 'badge-not-required' },
    pending: { label: 'بانتظار المتابعة', cls: 'badge-pending' },
    in_progress: { label: 'جاري المتابعة', cls: 'badge-retry' },
    callback_done_answered: { label: 'تم الرد', cls: 'badge-callback-answered' },
    callback_done_no_answer: { label: 'لم يرد', cls: 'badge-callback-no-answer' },
    retry_later: { label: 'إعادة محاولة', cls: 'badge-retry' },
    closed: { label: 'مغلقة', cls: 'badge-closed' }
  };
  const info = map[s] || { label: s, cls: '' };
  return (
    <span>
      <span className={`badge ${info.cls}`}>{info.label}</span>
      {s === 'in_progress' && followUpBy && (<span className="crm-by">({followUpBy})</span>)}
      {autoDetected && <span className="auto-detected" style={{ marginRight: 4 }}>تلقائي</span>}
    </span>
  );
};

const MyCallsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ pending: [], history: [], stats: { pending: 0, answeredToday: 0, followUpsToday: 0 } });
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('7days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [selectedCall, setSelectedCall] = useState(null);
  const [followUpCall, setFollowUpCall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let from = '', to = '';
      if (customFrom || customTo) {
        if (customFrom) from = new Date(customFrom).toISOString();
        if (customTo) { const d = new Date(customTo); d.setHours(23,59,59,999); to = d.toISOString(); }
      } else {
        const r = computeRange(activeFilter); from = r.from; to = r.to;
      }
      const params = new URLSearchParams();
      if (from) params.set('dateFrom', from);
      if (to) params.set('dateTo', to);
      const res = await api.get(`/calls/my/calls?${params.toString()}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [activeFilter, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);
  // Item 6: skip the 30s refresh while a modal (this page's or a nested CRM one)
  // is open, so it can't unmount an open modal and lose typed data.
  const pageModalOpen = useRef(false);
  pageModalOpen.current = !!(selectedCall || followUpCall);
  useEffect(() => {
    const t = setInterval(() => {
      if (pageModalOpen.current || anyModalOpen()) return;
      load();
    }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const handleCall = (n) => window.open(`https://ososenjaz.3cx.asia/#/people/phonedialog/${n}`, '_blank');

  const handleLock = async (call) => {
    try { await updateFollowUp(call.id, { followUpStatus: 'in_progress' }); load(); }
    catch (e) { alert(e.response?.data?.error || 'فشل التعليق'); }
  };

  const handleUnlock = async (call) => {
    try { await updateFollowUp(call.id, { followUpStatus: 'pending' }); load(); }
    catch (e) { alert(e.response?.data?.error || 'فشل فك التعليق'); }
  };

  const pickQuick = (key) => { setCustomFrom(''); setCustomTo(''); setActiveFilter(key); };

  // --- CRM layer (added) ---------------------------------------------------
  // One request builds a phone -> active-opportunities map for every row,
  // instead of one lookup per row.
  const [crmActiveByPhone, setCrmActiveByPhone] = useState({});
  // Phones with a NAMED client profile — drives the الملف / حفظ العميل button.
  const [crmSavedPhones, setCrmSavedPhones] = useState(null);
  const loadCrmOpportunities = useCallback(() => {
    crmListOpportunities()
      .then((res) => {
        const map = {};
        res.data.opportunities
          .filter((o) => !CLOSED_STAGES.includes(o.crm_stage))
          .forEach((o) => { (map[o.phone_number] = map[o.phone_number] || []).push(o); });
        setCrmActiveByPhone(map);
      })
      .catch(() => setCrmActiveByPhone({}));
    crmListClients()
      .then((res) => setCrmSavedPhones(new Set(
        res.data.clients.filter((c) => (c.full_name || '').trim()).map((c) => c.phone_number)
      )))
      .catch(() => setCrmSavedPhones(new Set()));
  }, []);
  useEffect(() => { loadCrmOpportunities(); }, [loadCrmOpportunities]);
  // ---------------------------------------------------------------------------

  // Batch 5 (styling only): same icon / label / value, now a calm white tile —
  // token surface + hairline border + subtle shadow, with the colour carried by
  // a soft icon chip instead of a heavy 4px bar.
  const StatCard = ({ icon, color, tint, label, value }) => (
    <div className="crm-stat">
      <div className="ico" style={{ background: tint }}>
        {React.cloneElement(icon, { size: 19, color })}
      </div>
      <div>
        <div className="lbl">{label}</div>
        <div className="val">{value}</div>
      </div>
    </div>
  );

  const renderRow = (call) => {
    const isLocked = call.followUpStatus === 'in_progress';
    const isMine = call.followUpByExtension === user?.extension;
    const canLock = call.followUpStatus === 'pending' || call.followUpStatus === 'retry_later' || call.followUpStatus === 'callback_done_no_answer';
    const isAdmin = user?.role === 'admin';
    const isCompleted = ['callback_done_answered','callback_done_no_answer','closed'].includes(call.followUpStatus);
    const showUpdate = call.followUpStatus !== 'not_required' && !isCompleted && (!(isLocked && !isMine && !isAdmin));

    return (
      <tr key={call.id} className={call.followUpStatus === 'in_progress' ? 'crm-row-locked' : undefined}>
        <td style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{call.callerNumber}</td>
        <td>{call.callerName || '-'}</td>
        <td><span className="badge badge-queue">{call.queueName || call.callSource}</span></td>
        <td style={{ fontSize: 13 }}>{formatDate(call.lastCallAt)}</td>
        <td>{getStatusBadge(call.lastCallStatus)}</td>
        <td>{call.answeredBy ? `${call.answeredBy} (${call.answeredByExtension})` : '-'}</td>
        <td>{getFollowUpBadge(call.followUpStatus, call.autoDetectedCallback, call.followUpBy)}</td>
        <td style={{ fontSize: 13 }}>{formatDate(call.followUpAt)}</td>
        {/* CRM cells (added). Same lock/mask rule as CallsPage: a call locked by
            another agent shows nothing here either. */}
        <td>
          {(() => {
            const lockedByOther = isLocked && !isMine && !isAdmin;
            if (lockedByOther) return null;
            return (
              <CrmCallCell
                call={call}
                activeByPhone={crmActiveByPhone}
                savedPhones={crmSavedPhones}
                onOpenClient={() => crmGo('crm-client')}
                onOpenOpportunity={() => crmGo('crm-opportunity')}
                onChanged={loadCrmOpportunities}
              />
            );
          })()}
        </td>
        <td>
          {(() => {
            const lockedByOther = isLocked && !isMine && !isAdmin;
            if (lockedByOther) return null;
            return <CrmTransferCell call={call} onChanged={loadCrmOpportunities} />;
          })()}
        </td>
        <td>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn btn-call btn-sm" onClick={() => handleCall(call.callerNumber)} title="اتصال عبر 3CX"><FiPhone size={12} /></button>
            <button className="btn btn-outline btn-sm" onClick={() => setSelectedCall(call)} title="تفاصيل"><FiEye size={12} /></button>
            {isLocked && (isMine || isAdmin) && (
              <button className="btn btn-warning btn-sm" onClick={() => handleUnlock(call)} title="فك التعليق"><FiLock size={12} /></button>
            )}
            {!isLocked && canLock && (
              <button className="btn btn-warning btn-sm" onClick={() => handleLock(call)} title="جاري المتابعة"><FiLock size={12} /></button>
            )}
            {showUpdate && (
              <button className="btn btn-primary btn-sm" onClick={() => setFollowUpCall(call)} title="تحديث المتابعة">تحديث</button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const TableHead = () => (
    // Padding / alignment now come from `.crm-calm .data-table th` (crm.css).
    <thead>
      <tr>
        <th>الرقم</th>
        <th>الاسم</th>
        <th>الكيو</th>
        <th>آخر مكالمة</th>
        <th>الحالة</th>
        <th>الموظف</th>
        <th>المتابعة</th>
        <th>وقت المتابعة</th>
        {/* CRM columns added — mirror CallsPage */}
        <th>الفرص</th>
        <th>تحويل المتابعة</th>
        <th>إجراء</th>
      </tr>
    </thead>
  );

  return (
    // Batch 5: calm/enterprise identity — styling scope only. No logic, column,
    // filter, ACL or data-flow change anywhere in this file.
    <div className="crm-calm crm-calm-page" style={{ padding: '24px 24px 88px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 className="page-title" style={{ margin: 0 }}>مكالماتي</h2>
        <button onClick={load} className="btn btn-outline btn-sm"><FiRefreshCw /> تحديث</button>
      </div>

      <div className="crm-stats">
        <StatCard icon={<FiClock />} color="#b54708" tint="var(--crm-warn-bg)" label="متابعات معلقة" value={data.stats.pending} />
        <StatCard icon={<FiPhone />} color="#067647" tint="var(--crm-success-bg)" label="واردة مردودة اليوم" value={data.stats.answeredToday} />
        <StatCard icon={<FiCheckCircle />} color="#4f46e5" tint="var(--crm-accent-soft)" label="متابعات اليوم" value={data.stats.followUpsToday} />
      </div>

      {data.pending.length > 0 && (
        <div className="crm-panel" style={{ marginBottom: 24 }}>
          <div className="crm-panel-head warn">
            ⏳ متابعات معلقة باسمي ({data.pending.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <TableHead />
              <tbody>{data.pending.map(renderRow)}</tbody>
            </table>
          </div>
        </div>
      )}

      <div className="crm-filterbar">
        {QUICK_FILTERS.map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${activeFilter === f.key && !customFrom && !customTo ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => pickQuick(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="lbl" style={{ marginRight: 12 }}>أو فترة مخصصة:</span>
        <span className="lbl">من</span><input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="filter-input" style={{ width: 150 }} />
        <span className="lbl">إلى</span>
        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="filter-input" style={{ width: 150 }} />
        {(customFrom || customTo) && (
          <button className="btn btn-outline btn-sm" onClick={() => { setCustomFrom(''); setCustomTo(''); }}>مسح</button>
        )}
      </div>

      <div className="crm-panel">
        <div className="crm-panel-head">
          سجل مكالماتي ({data.history.length})
        </div>
        {loading ? (
          <div className="crm-empty">جاري التحميل...</div>
        ) : data.history.length === 0 ? (
          <div className="crm-empty">لا توجد مكالمات في هذه الفترة</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <TableHead />
              <tbody>{data.history.map(renderRow)}</tbody>
            </table>
          </div>
        )}
      </div>

      {selectedCall && (
        <CallDetailModal call={selectedCall} onClose={() => setSelectedCall(null)} onUpdate={load} currentUser={user} />
      )}
      {followUpCall && (
        <FollowUpModal call={followUpCall} onClose={() => setFollowUpCall(null)} onSubmit={async (id, data) => { await updateFollowUp(id, data); setFollowUpCall(null); load(); }} />
      )}
    </div>
  );
};

export default MyCallsPage;
